import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { useWorkspace } from '../../store/WorkspaceContext';
import { usePolling } from '../../hooks/useDraft';
import { ticketApi } from '../../api/ticket.api';
import { BASE_MEDIA_URL } from '../../api/client';
import { ROLE_LABELS } from '../../utils/roleHelpers';
import { isWorkspaceEligiblePath, workspaceTabTitle } from '../../utils/workspaceConfig';
import { getMenuPreferencesCached, applyMenuPreferences } from '../../utils/menuPreferences';
import { getFallbackMenuItems, fetchRoleMenuItems } from '../../utils/menuAccess';
import { toPersian, formatPersianDateLong } from '../../utils/persianNumber';
import { X, CaretLeft, CaretDown } from '@phosphor-icons/react';

// Keys here are the VALUE strings get_menu_item actually returns
// (e.g. {'Ticket List': 'manager_ticket_view'}) — not the display labels.
const PATH_MAP = {
  home: '/home',
  profile_page: '/dashboard',
  edit_my_info: '/profile/edit',
  edit_info: '/profile/edit',
  daily_report: '/reports/daily',
  manager_daily_report: '/reports/manager',
  my_feedback_info: '/feedback/mine',
  employee_feedback_page: '/feedback/team',
  feedback: '/feedback',
  ticket_list: '/dashboard',
  ticket_form: '/tickets/new',
  send_ticket: '/tickets/new',
  smart_help: '/help',
  manager_ticket_view: '/tickets',
  referred_out_tickets: '/tickets/referred',
  dispatcher_ticket_view: '/dispatcher',
  assigned_unanswered_tickets: '/dispatcher/assigned',
  ai_route_tickets: '/dispatcher/ai-routing',
  ai_services_monitor: '/settings/ai-services',
  sms_campaigns: '/sms-campaigns',
  employee_dashboard: '/dashboard',
  my_dashboard: '/dashboard',
  manager_mission_view: '/missions/define',
  mission_list: '/missions/define',
  my_missions: '/missions/my',
  unseen_response_message: '/dashboard',
  unseen_message: '/dashboard',
  manager_user_unseen_ticket: '/dashboard',
  simple_user_unseen_ticket: '/dashboard',
  message_list: '/dashboard',
  messages: '/dashboard',
  responsed_ticket: '/tickets/responded',
  employee_responsed_ticket: '/tickets/responded',
  add_department_employee: '/users/add/employee',
  add_employee: '/users/add/employee',
  add_company_user: '/users/add/company',
  add_user: '/users/add/company',
  company_list_view: '/companies',
  department_list_view: '/departments',
  user_list_view: '/users',
  employee_list_view: '/employees',
  current_map: '/map',
  add_place: '/map/add-place',
  add_department: '/departments/add',
  add_company: '/companies/add',
  distance_report: '/reports/distance',
  presence_report: '/reports/presence',
  daily_report_routing: '/dispatcher/daily-report-routing',
  invoice_list: '/reports/invoices',
  activation_history: '/activation-history',
  setting: '/settings',
  menu_order: '/settings/menu',
  daily_report_items: '/settings/daily-report-items',
};

const FA_LABELS = {
  Home: 'خانه', 'My Dashboard': 'داشبورد', Tickets: 'تیکت‌ها',
  'Edit Info': 'ویرایش اطلاعات', 'Daily Report': 'گزارش روزانه',
  FeedBack: 'نظرسنجی', 'Ticket List': 'همه',
  'All Tickets': 'همه', 'لیست تیکت‌ها': 'همه', 'لیست همه تیکت‌ها': 'همه',
  'Referred Tickets': 'تیکت‌های ارجاع‌شده',
  Messages: 'پیام‌ها', 'Message List': 'لیست پیام‌ها',
  'Current Map': 'نقشه فعلی', Setting: 'تنظیمات', Logout: 'خروج',
  'Mission List': 'ماموریت‌ها', 'Responsed Ticket': 'تیکت‌های پاسخ‌داده',
  'Assigned Tickets': 'تیکت‌های ارجاع‌شده',
  'AI Route Tickets': 'ارجاع با هوش مصنوعی',
  'AI Services': 'سرویس‌های هوش مصنوعی',
  'SMS Campaigns': 'کمپین پیامکی',
  'Add Employee': 'افزودن کارمند', 'Send Ticket': 'ارسال تیکت',
  'Smart Help': 'راهنمای هوشمند',
  'Add User': 'افزودن کاربر', Lists: 'لیست‌ها',
  'Company List': 'شرکت‌ها', 'Department List': 'دپارتمان‌ها', 'User List': 'کاربران',
  'Employee List': 'کارمندان', Add: 'افزودن',
  'Add Place': 'افزودن مکان', 'Add Department': 'افزودن دپارتمان',
  'Add Company': 'افزودن شرکت', Reports: 'گزارش‌ها',
  'Distance Report': 'گزارش مسافت', 'Presence Report': 'گزارش حضور',
  'Daily Report Routing': 'مسیریابی گزارش روزانه',
  'Invoice List': 'لیست فاکتورها',
  'My Missions': 'ماموریت‌های من', 'Department Daily Reports': 'گزارش‌های روزانه دپارتمان',
  'Activation History': 'تاریخچه فعال‌سازی',
  'Report Parts': 'بخش‌های گزارش',
  'Menu Layout': 'ترتیب منو',
};
export const fa = (en) => FA_LABELS[en] || en;

const ICONS = {
  home: 'M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z',
  dashboard: 'M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z',
  ticket: 'M2 9a3 3 0 000 6v2a2 2 0 002 2h16a2 2 0 002-2v-2a3 3 0 000-6V7a2 2 0 00-2-2H4a2 2 0 00-2 2v2z',
  message: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  report: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6',
  map: 'M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z M12 10a1 1 0 100-2 1 1 0 000 2z',
  settings: 'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
  logout: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9',
  mission: 'M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  feedback: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  building: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10',
  users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 7a4 4 0 100 8 4 4 0 000-8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
  plus: 'M12 5v14M5 12h14',
  ai: 'M12 2a10 10 0 110 20A10 10 0 0112 2zm0 6v4l3 3',
};

function LiveClock({ s }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const date = formatPersianDateLong(now);
  const time = now.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div style={s.clock}>
      <div style={s.clockTime}>{time}</div>
      <div style={s.clockDate}>{date}</div>
    </div>
  );
}

export function Icon({ name, size = 16, style: extra }) {
  const d = ICONS[name] || ICONS.home;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={extra}>
      <path d={d} />
    </svg>
  );
}

// Small colored backdrop behind each nav icon — brightens to a solid accent
// chip when its item is active, giving the menu a distinct focal point
// instead of a flat row of same-weight icons.
function IconChip({ name, active, bg, activeBg }) {
  return (
    <span style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 26, height: 26, borderRadius: 8, flexShrink: 0,
      background: active ? (activeBg || 'linear-gradient(135deg,#29AAE1,#233A7A)') : (bg || 'rgba(255,255,255,0.06)'),
      color: active ? '#fff' : 'inherit',
      boxShadow: active ? '0 2px 8px rgba(41,170,225,0.35)' : 'none',
      transition: 'background 0.18s, box-shadow 0.18s',
    }}>
      <Icon name={name} size={14} />
    </span>
  );
}

export function getIcon(label) {
  const l = label.toLowerCase();
  if (label.includes('راهنما') || l.includes('smart help') || l.includes('help')) return 'ai';
  if (l.includes('dashboard') || l.includes('my dashboard')) return 'dashboard';
  if (l.includes('ticket') || l.includes('send ticket')) return 'ticket';
  if (l.includes('message')) return 'message';
  if (l.includes('report') || l.includes('daily')) return 'report';
  if (l.includes('map') || l.includes('distance') || l.includes('presence')) return 'map';
  if (l.includes('setting')) return 'settings';
  if (l.includes('logout')) return 'logout';
  if (l.includes('mission')) return 'mission';
  if (l.includes('edit')) return 'edit';
  if (l.includes('feedback')) return 'feedback';
  if (l.includes('list')) return 'list';
  if (l.includes('company')) return 'building';
  if (l.includes('user') || l.includes('employee')) return 'users';
  if (l.includes('add') || l.includes('place') || l.includes('department')) return 'plus';
  if (l.includes('home')) return 'home';
  return 'list';
}

// get_menu_item returns a plain object keyed by display label, e.g.
// {'Home': 'home', 'Ticket List': 'manager_ticket_view', 'Lists': {...}},
// plus a role-specific 'count' entry that isn't a menu item at all — this
// turns that into the {label, key, children} shape renderItem expects.
export function normalizeMenu(dict) {
  if (!dict || typeof dict !== 'object' || Array.isArray(dict)) return [];
  return Object.entries(dict)
    .filter(([label]) => label !== 'count')
    .map(([label, value]) => (
      value && typeof value === 'object' && !Array.isArray(value)
        ? { label, children: normalizeMenu(value) }
        : { label, key: value }
    ));
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { sidebarOpen, setSidebarOpen } = useUI();
  const location = useLocation();
  const navigate = useNavigate();
  // Only non-null for simple_employee/manager_employee (AppLayout only
  // mounts <WorkspaceProvider> for those roles) - every other role's
  // Sidebar behaves exactly as before.
  const workspace = useWorkspace();
  const [menuItems, setMenuItems] = useState([]);
  const [menuPrefs, setMenuPrefs] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [unread, setUnread] = useState(0);
  const [collapsed, setCollapsed] = useState({});
  const [avatarError, setAvatarError] = useState(false);

  // Reset the broken-image fallback whenever the user (or their picture)
  // actually changes, so a stale failure doesn't stick around forever.
  useEffect(() => { setAvatarError(false); }, [user?.image_name]);

  // Close the mobile drawer whenever the route changes (desktop ignores this
  // since the sidebar there is always visible regardless of sidebarOpen).
  useEffect(() => { setSidebarOpen(false); }, [location.pathname, setSidebarOpen]);

  const toPath = useCallback((key) => {
    const k = key?.toLowerCase().replace(/\s+/g, '_');
    return PATH_MAP[k] || `/${k}`;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadMenu = async () => {
      const { items, unread: count } = await fetchRoleMenuItems(user?.type, toPath, normalizeMenu);
      if (cancelled) return;
      setMenuItems(items);
      if (typeof count === 'number') setUnread(count);
    };

    loadMenu();
    // Saved order/hide state - refetch when user/role changes; cache is
    // invalidated on login/logout so admin-side menu edits show up without
    // forcing a full logout.
    getMenuPreferencesCached().then((prefs) => { if (!cancelled) setMenuPrefs(prefs); }).catch(() => {});

    return () => { cancelled = true; };
  }, [user?.type, user?.username, user?.employee_id, user?.user_id, location.pathname, toPath]);

  // main_menu_index (see utils/menuPreferences.js) is this item's position
  // in the RAW top-level array from get_menu_item, before any
  // reordering/hiding - must be computed off menuItems as fetched, not
  // after applyMenuPreferences already reordered it.
  const { visible: visibleMenuItems, hidden: hiddenMenuItems } = applyMenuPreferences(menuItems, menuPrefs);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await ticketApi.getUnreadCount();
      const d = res.data;
      setUnread(d?.count ?? d?.unseen_count ?? 0);
    } catch {}
  }, []);

  // get_unreaded_response_message (see getUnreadedResonseMessage) is
  // manager_employee only - polling it for every role 403s pointlessly.
  usePolling(fetchUnread, 15000, user?.type === 'manager_employee');

  const isActive = (key) => location.pathname === toPath(key);

  const toggleGroup = (label) => setCollapsed((c) => ({ ...c, [label]: !c[label] }));

  const handleLogout = async () => {
    await logout(navigate);
  };

  const s = styles;

  return (
    <aside className={`pj-sidebar${sidebarOpen ? ' open' : ''}`} style={s.sidebar}>
      {/* Brand */}
      <div style={s.brand}>
        <div style={s.logoRing}>
          <img src="/pejvak-mark.svg" alt="پژواک رایانه" width={30} height={30} />
        </div>
        <div>
          <div style={s.brandName}>پژواک رایانه</div>
          <div style={s.brandSub}>سامانه CRM</div>
        </div>
        <button className="pj-sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="بستن منو"
          style={{ marginRight: 'auto', width: 32, height: 32, borderRadius: 8, alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
          <X size={16} weight="regular" />
        </button>
      </div>

      <LiveClock s={s} />

      <div style={s.divider} />

      {/* User pill */}
      {user && (
        <Link to="/profile" className="pj-user-pill" style={s.userPill}>
          {!avatarError && user.image_name ? (
            <img
              src={BASE_MEDIA_URL + user.image_name}
              alt={user.username}
              onError={() => setAvatarError(true)}
              style={{ ...s.userAvatar, background: '#29AAE1', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ ...s.userAvatar, background: '#29AAE1' }}>
              {user.username?.slice(0, 2) || '؟'}
            </div>
          )}
          <div style={{ overflow: 'hidden' }}>
            <div style={s.userName}>{user.username}</div>
            <div style={s.userRole}>{ROLE_LABELS[user.type] || user.type}</div>
          </div>
        </Link>
      )}

      {/* AI Center — single entry; sub-pages reachable from inside */}
      {user?.type === 'manager_employee' && (
        <Link to="/ai/manager" className="pj-nav-item" style={{ ...s.navItem, background: 'linear-gradient(135deg,rgba(139,92,246,0.20),rgba(124,58,237,0.10))', color: '#d8c8fb', margin: '8px 12px 4px', fontWeight: 700 }}>
          <IconChip name="ai" active activeBg="linear-gradient(135deg,#a78bfa,#7c3aed)" />
          <span>مرکز هوش مصنوعی</span>
          <span style={{ marginRight: 'auto', background: '#7c3aed', color: '#fff', fontSize: 9, padding: '2px 6px', borderRadius: 8, fontWeight: 800, letterSpacing: 0.5 }}>AI</span>
        </Link>
      )}

      {/* Dispatcher department overview */}
      {user?.type === 'dispatcher' && (
        <Link to="/dispatcher/departments" className="pj-nav-item" style={{ ...s.navItem, margin: '8px 12px 4px' }}>
          <IconChip name="building" />
          <span>نمای دپارتمان‌ها</span>
        </Link>
      )}

      {/* Places management - dispatcher/manager_employee could only add a
          place before, never see or correct one afterward */}
      {(user?.type === 'dispatcher' || user?.type === 'manager_employee') && (
        <Link to="/map/places" className="pj-nav-item" style={{ ...s.navItem, margin: '2px 12px 4px' }}>
          <IconChip name="map" />
          <span>مدیریت مکان‌ها</span>
        </Link>
      )}

      <div style={s.divider} />

      {/* Dynamic menu from backend */}
      <nav style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
        {menuItems.length > 0
          ? visibleMenuItems.map((item, i) => renderItem(item, i, isActive, toPath, unread, toggleGroup, collapsed, s, workspace, navigate))
          : <FallbackMenu role={user?.type} unread={unread} s={s} />
        }
        {hiddenMenuItems.length > 0 && (
          <div>
            <button onClick={() => setShowHidden((v) => !v)} className="pj-nav-item"
              style={{ ...s.navItem, width: 'calc(100% - 24px)', margin: '2px 12px', color: '#64748b' }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 4, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }}><CaretDown size={10} weight="bold" /></span>
              <span>موارد مخفی‌شده</span>
              <span style={{ marginRight: 'auto', fontSize: 10, color: '#64748b' }}>{toPersian(hiddenMenuItems.length)}</span>
            </button>
            {showHidden && (
              <div style={{ paddingRight: 16, opacity: 0.75 }}>
                {hiddenMenuItems.map((item, i) => renderItem(item, `hidden-${i}`, isActive, toPath, unread, toggleGroup, collapsed, s, workspace, navigate))}
              </div>
            )}
          </div>
        )}
      </nav>

      <div style={s.divider} />

      {/* Logout */}
      <button onClick={handleLogout} className="pj-nav-item" style={{ ...s.navItem, color: '#f87171', margin: '8px 12px 16px', width: 'calc(100% - 24px)' }}>
        <IconChip name="logout" bg="rgba(248,113,113,0.14)" />
        <span>خروج از حساب</span>
      </button>
    </aside>
  );
}

function renderItem(item, i, isActive, toPath, unread, toggleGroup, collapsed, s, workspace, navigate) {
  const label = item.label || item.name || '';
  const children = item.children || item.sub_items || [];
  const hasChildren = children.length > 0;
  const active = isActive(item.key);
  const isMsg = ['Messages', 'Message List'].includes(label);

  if (hasChildren) {
    const open = !collapsed[label];
    return (
      <div key={i}>
        <button onClick={() => toggleGroup(label)} className="pj-nav-item"
          style={{ ...s.navItem, width: 'calc(100% - 24px)', margin: '2px 12px' }}>
          <IconChip name={getIcon(label)} />
          <span>{fa(label)}</span>
          <span style={{ marginRight: 'auto', display: 'flex', transition: 'transform 0.2s', transform: open ? 'rotate(-90deg)' : 'rotate(0)' }}><CaretLeft size={12} weight="bold" /></span>
        </button>
        {open && (
          <div style={s.subGroup}>
            {children.map((child, j) => renderItem(child, j, isActive, toPath, unread, toggleGroup, collapsed, s, workspace, navigate))}
          </div>
        )}
      </div>
    );
  }

  if (label === 'Logout' || item.key === 'Logout') return null;

  const path = toPath(item.key);
  const badge = isMsg && unread > 0 && (
    <span style={s.unreadBadge}>{toPersian(unread)}</span>
  );

  // Employee ticket/mission menu items open as a workspace tab instead of
  // replacing the page - requirement 2. Everything else (settings, profile,
  // reports, feedback, ...) keeps its normal full-page <Link> navigation
  // unchanged, for every role including workspace ones.
  if (workspace && isWorkspaceEligiblePath(path)) {
    return (
      <button
        key={i}
        onClick={() => { workspace.openTab(path, workspaceTabTitle(path)); navigate('/dashboard'); }}
        className="pj-nav-item"
        style={{ ...s.navItem, width: 'calc(100% - 24px)', margin: '2px 12px', ...(active ? s.navItemActive : {}) }}
      >
        <IconChip name={getIcon(label)} active={active} />
        <span>{fa(label)}</span>
        {badge}
      </button>
    );
  }

  return (
    <Link key={i} to={path} className="pj-nav-item" style={{
      ...s.navItem,
      margin: '2px 12px',
      ...(active ? s.navItemActive : {}),
    }}>
      <IconChip name={getIcon(label)} active={active} />
      <span>{fa(label)}</span>
      {badge}
    </Link>
  );
}

function FallbackMenu({ role, unread, s }) {
  const location = useLocation();
  const items = getFallbackMenuItems(role).map((item) => (
    item.id === 'messages' ? { ...item, badge: unread } : item
  ));
  return (
    <>
      {items.map(({ id, to, icon, label, badge }) => (
        <Link key={id} to={to} className="pj-nav-item" style={{ ...s.navItem, margin: '2px 12px', ...(location.pathname === to ? s.navItemActive : {}) }}>
          <IconChip name={icon} active={location.pathname === to} />
          <span>{label}</span>
          {badge > 0 && <span style={s.unreadBadge}>{toPersian(badge)}</span>}
        </Link>
      ))}
    </>
  );
}

const styles = {
  sidebar: {
    position: 'fixed', top: 0, right: 0, bottom: 0,
    insetInlineEnd: 0,
    width: 'var(--sidebar-width)',
    background: 'var(--sidebar-bg)',
    display: 'flex', flexDirection: 'column',
    zIndex: 100, overflow: 'hidden',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
  },
  brand: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '22px 16px',
    position: 'relative', overflow: 'hidden',
    background: 'radial-gradient(circle at 80% 0%, rgba(41,170,225,0.35), transparent 55%), linear-gradient(180deg, #1E4C8C 0%, #172D67 100%)',
  },
  logoRing: {
    width: 46, height: 46, borderRadius: 12,
    background: 'linear-gradient(145deg, #ffffff 0%, #bfe0f5 55%, #29AAE1 100%)',
    boxShadow: '0 4px 14px rgba(41,170,225,0.40)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  brandName: { fontSize: 15, fontWeight: 700, color: '#f1f5f9' },
  brandSub: { fontSize: 10, color: '#64748b', marginTop: 2 },
  divider: { height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 16px' },
  clock: {
    margin: '0 16px 12px',
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    textAlign: 'center',
  },
  clockTime: { fontSize: 15, fontWeight: 700, color: '#7cc9ec', fontVariantNumeric: 'tabular-nums' },
  clockDate: { fontSize: 10.5, color: '#94a3b8', marginTop: 3 },
  userPill: {
    display: 'flex', alignItems: 'center', gap: 10,
    margin: '12px 12px 8px',
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 12, cursor: 'pointer',
    textDecoration: 'none',
    border: '1px solid rgba(255,255,255,0.08)',
    transition: 'background 0.15s, border-color 0.15s',
  },
  userAvatar: {
    width: 42, height: 42, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 14, fontWeight: 700, flexShrink: 0,
    boxShadow: '0 0 0 2px rgba(41,170,225,0.35)',
  },
  userName: { fontSize: 13, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole: { fontSize: 10, color: '#94a3b8', marginTop: 1 },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', borderRadius: 10,
    fontSize: 13, fontWeight: 500,
    textDecoration: 'none', transition: 'all 0.15s',
    cursor: 'pointer', border: 'none',
    fontFamily: 'inherit', width: 'calc(100% - 24px)',
  },
  navItemActive: {
    background: 'linear-gradient(135deg, rgba(41,170,225,0.20), rgba(35,58,122,0.30))',
    color: '#fff',
    fontWeight: 700,
    boxShadow: 'inset 0 0 0 1px rgba(41,170,225,0.35)',
  },
  subGroup: {
    paddingRight: 14,
    marginRight: 12,
    borderRight: '1px dashed rgba(255,255,255,0.10)',
  },
  unreadBadge: {
    marginRight: 'auto',
    background: 'linear-gradient(135deg,#f87171,#ef4444)',
    color: '#fff', fontSize: 10, padding: '1px 7px',
    borderRadius: 10, fontWeight: 700,
    boxShadow: '0 0 0 3px rgba(239,68,68,0.15)',
  },
};
